import { Box, IconButton, Nav, Logo as SambaLogo, Shimmer, ShimmerText, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { SidebarHeaderQuery } from './graphql/query.sidebarHeader.generated';
import { Logo } from './Logo';

import { urlBuilder } from '../../../Shared/urlBuilder';
import { AdvisorContactLink } from '../AdvisorContactLink';
import { NavLink } from '../nav-link/NavLink';

type Props = {
  loading: boolean;
  advisor: SidebarHeaderQuery['advisor'];
  closeSidebar: () => void;
};

export const SidebarHeader: React.FC<Props> = ({ loading, advisor, closeSidebar }) => {
  if (loading) {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 'space.300',
          paddingBottom: 'space.300',
          alignItems: 'flex-start',
        }}
        data-color-mode="dark"
      >
        <Box
          css={{
            display: 'flex',
            gap: 'space.100',
            alignItems: 'center',
          }}
        >
          <Shimmer css={{ height: 'size.200', width: 'size.400', borderRadius: 'medium' }} />
          <ShimmerText textStyle="text25" css={{ width: 'size.900', flexShrink: 1 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box css={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'start', px: 'space.150' }}>
      {advisor && (
        <Box>
          {advisor.__typename === 'InstitutionalAdvisor' && (
            <>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <SambaAutoLink aria-label="Return to Betterment Home Page" to={urlBuilder.appHome.absolute()}>
                  {advisor.logo && (
                    <Logo>
                      <img src={advisor.logo.dark} alt="Advisor firm logo" />
                    </Logo>
                  )}
                </SambaAutoLink>
                <IconButton
                  css={{ color: 'icon.onPrimary', my: 'space.300', md: { display: 'none' } }}
                  name="X"
                  label="Close navigation"
                  onClick={closeSidebar}
                />
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'column', mb: 'space.200' }}>
                <NavLink.Text>Your advisor</NavLink.Text>
                <Text color="text.onPrimary">{advisor.fullName}</Text>
              </Box>

              <Box css={{ mb: 'space.100' }}>
                {advisor.phoneNumber && (
                  <AdvisorContactLink
                    icon="Phone"
                    contactInfo={advisor.phoneNumber}
                    href={`tel:${advisor.phoneNumber}`}
                  />
                )}

                <AdvisorContactLink icon="Mail" contactInfo={advisor.email} href={`mailto:${advisor.email}`} />
              </Box>
            </>
          )}
          {advisor.__typename === 'PremiumAdvisor' && (
            <Box>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <SambaAutoLink aria-label="Return to Betterment Home Page" to={urlBuilder.appHome.absolute()}>
                  {advisor.logo && (
                    <Logo>
                      <img src={advisor.logo.dark} alt="Betterment premium logo" />
                    </Logo>
                  )}
                </SambaAutoLink>
                <IconButton
                  css={{ color: 'icon.onPrimary', my: 'space.300', md: { display: 'none' } }}
                  name="X"
                  label="Close navigation"
                  onClick={closeSidebar}
                />
              </Box>

              <NavLink.Text>Talk to an advisor</NavLink.Text>

              <AdvisorContactLink icon="Mail" contactInfo={advisor.email} href={`mailto:${advisor.email}`} />

              {advisor.calendarLink && (
                <AdvisorContactLink
                  icon="Calendar"
                  contactInfo="Schedule a chat"
                  href={advisor.calendarLink}
                  target="_blank"
                  rel="noreferrer"
                />
              )}
            </Box>
          )}
          <Nav.Separator />
        </Box>
      )}
      {!advisor && (
        <>
          <SambaAutoLink aria-label="Return to Betterment Home Page" to={urlBuilder.appHome.absolute()}>
            <Logo>
              <SambaLogo type="signatureWhite" css={{ height: 'size.200' }} />
            </Logo>
          </SambaAutoLink>
          <IconButton
            css={{ color: 'icon.onPrimary', my: 'space.300', md: { display: 'none' } }}
            name="X"
            label="Close navigation"
            onClick={closeSidebar}
          />
        </>
      )}
    </Box>
  );
};
